// Soft-Delete-Confirm. Doc tippt „LÖSCHEN" als Bestätigung (Anti-Misklick).

import { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import type { AdminUserRow } from '@/lib/admin-users-api';

interface DeleteConfirmDialogProps {
  user: AdminUserRow;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}

export function DeleteConfirmDialog({ user, onClose, onConfirm }: DeleteConfirmDialogProps) {
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const canDelete = confirmText.trim().toUpperCase() === 'LÖSCHEN';

  async function submit() {
    if (!canDelete) return;
    setDeleting(true);
    try {
      await onConfirm(reason.trim());
    } finally {
      setDeleting(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.96, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="glass-strong w-[min(480px,92vw)] rounded-2xl border border-rose-500/30 p-5"
      >
        <div className="mb-3 flex items-center gap-2">
          <AlertTriangle className="size-5 text-rose-400" />
          <h2 className="text-base font-bold text-rose-300">Member entfernen?</h2>
        </div>
        <p className="mb-3 text-[12px] text-neutral-300">
          <span className="font-mono text-neutral-100">{user.email}</span> wird soft-gelöscht — taucht nicht mehr in der Liste auf, Memories + Journal bleiben aber als Referenzen erhalten. Reversibel.
        </p>
        <label className="block text-[11px] uppercase tracking-wider text-neutral-500">
          Grund (optional, landet im Audit-Log)
        </label>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="z.B. „Spam-Account"
          className="mb-3 mt-1 w-full rounded-md border border-white/10 bg-neutral-900/60 px-3 py-1.5 text-[12px] text-neutral-100 outline-none focus:border-rose-500/40"
        />
        <label className="block text-[11px] uppercase tracking-wider text-neutral-500">
          Zur Bestätigung „LÖSCHEN" tippen
        </label>
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          className="mb-3 mt-1 w-full rounded-md border border-white/10 bg-neutral-900/60 px-3 py-1.5 text-[12px] uppercase text-neutral-100 outline-none focus:border-rose-500/40"
        />
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-[12px] text-neutral-400 hover:text-neutral-200"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!canDelete || deleting}
            className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/15 px-3 py-1.5 text-[12px] font-semibold text-rose-300 hover:bg-rose-500/25 disabled:opacity-40"
          >
            {deleting ? <Loader2 className="size-3 animate-spin" /> : <Trash2 className="size-3" />}
            Entfernen
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
